import { SlideMutationAPI } from '../api/slide-mutation-api';
import { PageContext } from '../context/page-context';
import { SlideProps } from '../props/page/slide-props';
import { SlideStore } from '../store/slide-store';
import { PresentationEventTypes } from '../manager/event-manager/event-types';
import { PageDataModel } from './page-data-model';
import type { IThemeDataModel } from './theme-data-model';
import type { IEventManager } from '../manager/event-manager';
import type { INoteSlideDataModel } from './note-slide-data-model';
import type { ISlideData } from '../data';
import type { ISlideProps } from '../props/page/slide-props';
import type { RestrictedDataModel } from './data-model';
import type { ILayoutDataModel } from './layout-data-model';
import type { IMasterDataModel } from './master-data-model';
import type { PresentationDataModel } from './presentation-data-model';

export class SlideDataModel extends PageDataModel<ISlideData, ISlideProps> {
    public context: PageContext;
    public store: SlideStore;
    public api: SlideMutationAPI;
    public props: ISlideProps;
    /**
     * 是否正在加载分页，只在加载分页时为 true，加载完成后为 false
     */
    private loading = false;

    public constructor(data: ISlideData, events: IEventManager, parent: PresentationDataModel) {
        super(events, parent);
        this.context = new PageContext(data.type);

        this.initRelation(parent, data.layoutId);
        this.context.setPresentation(this.parent);
        this.context.setPage(this);
        this.context.setSlide(this);
        this.context.setTarget(this);

        this.store = new SlideStore(data, this.events, this.context, this);
        this.props = new SlideProps(this.store.data, this.context);
        this.api = new SlideMutationAPI(this, events);
    }

    public getNoteSlide(): INoteSlideDataModel | undefined {
        return this.parent.store.getNoteSlideById(this.store.data.noteSlideId ?? '');
    }

    public getLayout(): ILayoutDataModel | undefined {
        return this.parent.store.getLayoutById(this.store.data.layoutId);
    }

    public getMaster(): IMasterDataModel | undefined {
        const layout = this.getLayout();
        return layout?.getMaster();
    }

    public getDisplayPlaceholderChildren() {
        return this.store.getDisplayPlaceholderChildren();
    }

    public forEachDescendant(callback: (child: RestrictedDataModel) => void = () => void 0) {
        const noteSlide = this.getNoteSlide();
        if (noteSlide) {
            callback(noteSlide);
            noteSlide.forEachDescendant(callback);
        }
        if (this.timing) {
            callback(this.timing);
        }
        super.forEachDescendant(callback);
    }

    public changeLoadingState(isLoading: boolean) {
        this.loading = isLoading;
        this.events.emit(PresentationEventTypes.PageLoadingStateChange, isLoading, this.id);
    }

    public waitForLoading(cb: () => any) {
        if (!this.loading) {
            cb();
            return;
        }
        const off = this.events.on(
            PresentationEventTypes.PageLoadingStateChange,
            (isLoading: boolean, slideId: string) => {
                if (!isLoading && slideId === this.id) {
                    cb();
                    off();
                }
            },
        );
    }

    private initRelation(parent: PresentationDataModel, layoutId: string) {
        const layout = parent.store.getLayoutById(layoutId);
        const master = layout?.getMaster();
        const theme = master?.getTheme();
        if (layout) {
            this.context.setLayout(layout as ILayoutDataModel);
        }
        if (master) {
            this.context.setMaster(master as IMasterDataModel);
        }
        if (theme) {
            this.context.setTheme(theme as IThemeDataModel);
        }
    }
}

export { ISlideProps };
export type ISlideDataModel = RestrictedDataModel<SlideDataModel>;
